/**
 * Client side of the portal routing framework.
 *
 * The site is static: it does not — and cannot — authenticate anyone. All it
 * does is send requests to a same-origin path, which the edge (Vite in dev,
 * Netlify in production) proxies to the internal demonstration server. That
 * server owns authentication and authorisation. See docs/portal.md.
 */

/** Same-origin path that proxies through to the internal server. */
export const PORTAL_BASE_PATH: string =
  import.meta.env.VITE_PORTAL_BASE_PATH || '/portal';

/** Endpoint the internal server should expose for reachability checks. */
export const PORTAL_HEALTH_PATH = `${PORTAL_BASE_PATH}/health`;

export type PortalStatus = 'checking' | 'online' | 'offline';

const TIMEOUT_MS = 4000;

/** Probes the portal without surfacing why it failed. */
export async function probePortal(signal?: AbortSignal): Promise<PortalStatus> {
  const timeout = AbortSignal.timeout(TIMEOUT_MS);
  const composed = signal ? AbortSignal.any([signal, timeout]) : timeout;

  try {
    const response = await fetch(PORTAL_HEALTH_PATH, {
      method: 'GET',
      cache: 'no-store',
      credentials: 'omit',
      signal: composed,
    });
    // 401/403 still means the server is up and gating correctly.
    return response.ok || response.status === 401 || response.status === 403
      ? 'online'
      : 'offline';
  } catch {
    return 'offline';
  }
}
